import { ArchiveBoxArrowDownIcon } from '@heroicons/react/24/solid';
import { CheckCircle, ClipboardList, Download, XCircle } from 'lucide-react';

interface ActionButtonProps {
  onClick: () => void;
  buttonText: string;
  buttonType: 'acceptNext' | 'acceptSubmitting' | 'acceptApprove' | 'reject' | 'rework' | 'downloadReport';
  disable?: boolean;
  testIdIdentifier?: string;
  loadingText?: string; // Add loading prop
}

export const ActionButton: React.FC<ActionButtonProps> = (props: ActionButtonProps) => {
  const acceptNextButtonScheme = "bg-sky-500 hover:bg-sky-600 focus:ring-sky-500";
  const acceptSubmittingButtonScheme = "bg-gray-300 cursor-not-allowed focus:ring-sky-500";
  const acceptApproveButtonScheme = "bg-teal-500 hover:bg-teal-600 focus:ring-teal-500";
  const rejectButtonScheme = "bg-rose-500 hover:bg-rose-600 focus:ring-rose-500";
  const reworkButtonScheme = "bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-500";
  const downloadReportButtonScheme = "bg-indigo-500 hover:bg-indigo-600 focus:ring-indigo-500";
  const scheme = {
    acceptNext: acceptNextButtonScheme,
    acceptSubmitting: acceptSubmittingButtonScheme,
    acceptApprove: acceptApproveButtonScheme,
    reject: rejectButtonScheme,
    rework: reworkButtonScheme,
    downloadReport: downloadReportButtonScheme,
  };
  const buttonContent = {
    acceptNext: (
      <>
        <ArchiveBoxArrowDownIcon className="h-4 w-4 mr-2" />
      </>
    ),
    acceptSubmitting: (
      <>
        <ArchiveBoxArrowDownIcon className="h-4 w-4 mr-2" />
      </>
    ),
    acceptApprove: (
      <>
        <CheckCircle className="h-4 w-4 mr-2" />
      </>
    ),
    reject: (
      <>
        <XCircle className="h-4 w-4 mr-2" />
      </>
    ),
    rework: (
      <>
        <ClipboardList className="h-4 w-4 mr-2" />
      </>
    ),
    downloadReport: (
      <>
        <Download className="h-4 w-4 mr-2" />
      </>
    ),
  };
  // Add faded style when disabled or loading
  const fadedStyle = props.disable || !!props.loadingText ? 'opacity-50 cursor-not-allowed' : '';

  return (
    <button
      onClick={props.onClick}
      disabled={props.disable || !!props.loadingText || false} // Disable button when loading
      className={`${scheme[props.buttonType]} ${fadedStyle} w-full shadow-sm hover:shadow-md text-base flex my-0 py-3 px-4 rounded-lg flex items-center justify-center text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200`}
      data-cy={props.testIdIdentifier}
    >
      {!!props.loadingText ? (
        <span className="flex items-center">
          <svg
            className="mr-3 w-4 h-4 animate-spin"
            viewBox="0 0 24 24"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              strokeDasharray="25 50"
              strokeLinecap="round"
            />
          </svg>
          {props.loadingText}
        </span>
      ) : (
        <>
          {buttonContent[props.buttonType]}
          <span>{props.buttonText}</span>
        </>
      )}
    </button>
  );
};

export default ActionButton;